import { type ComponentType } from 'react';
import { FixedWidthFieldRenderer, type FieldRendererProps } from './base';
import { BooleanFieldRenderer } from './bool';
import { DurationFieldRenderer } from './duration';
import { EnumFieldRenderer } from './enum';
import { ReceiptFieldRenderer } from './receipt';
import { SkillMatchesFieldRenderer } from './skillMatches';
import { TransactionQueueFieldRenderer } from './transaction';
import { VideoLinkItemsFieldRenderer } from './videoLinkItems';

export type FieldRendererComponent = ComponentType<FieldRendererProps>;

/**
 * Renderers keyed by the field's schema `type` from event-standard.
 * Used when no field-name-specific renderer exists.
 */
export const GenericTypeRenderers: { [key: string]: FieldRendererComponent } = {
  boolean: BooleanFieldRenderer,
  enum: EnumFieldRenderer,
  'date-time': FixedWidthFieldRenderer,
  number: FixedWidthFieldRenderer,
  string: FixedWidthFieldRenderer,
  object: FixedWidthFieldRenderer,
};

/**
 * Renderers keyed by the exact field name (e.g. `details.Receipt`, `videoLinkItems`).
 * Takes priority over {@link GenericTypeRenderers}.
 */
export const SpecificFieldRenderers: { [key: string]: FieldRendererComponent } = {
  'details.Receipt': ReceiptFieldRenderer,
  queue: TransactionQueueFieldRenderer,
  transaction: TransactionQueueFieldRenderer,
  videoLinkItems: VideoLinkItemsFieldRenderer,
  'details.Vehicle dwell time': DurationFieldRenderer,
  'details.Time without vehicle': DurationFieldRenderer,
  'details.Vehicle departure time': DurationFieldRenderer,
  'details.skillMatches': SkillMatchesFieldRenderer,
};

/**
 * Resolve the renderer for a field: specific name wins, then generic type.
 */
export function getFieldRenderer(
  fieldName: string,
  fieldType: string,
): FieldRendererComponent | undefined {
  return SpecificFieldRenderers[fieldName] || GenericTypeRenderers[fieldType];
}
